import AddCircleOutlineRoundedIcon from '@mui/icons-material/AddCircleOutlineRounded';
import {
  Box,
  IconButton,
  ListItemButton,
  ListItemText,
  Typography,
  useTheme,
} from '@mui/material';
import * as Sentry from '@sentry/nextjs';
import Fuse from 'fuse.js';
import { useTranslation } from 'next-i18next';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { FC } from 'react';
import { useDebounce } from 'use-debounce';

import CustomSearchbar from 'components/CustomSearchBar';
import ErrorText from 'components/ErrorText';

import { useAppDispatch, useAppSelector } from 'redux-store';
import { setPairDevicesToRfidsModal } from 'redux-store/slices/ui';
import getAllRfidsThunk from 'redux-store/thunks/rfids/getAllRfidsThunk';
import unPairDevicesFromRfidsThunk from 'redux-store/thunks/rfids/unPairDevicesFromRfidsThunk';

import type { AssignDevicesToMembersModalStageProps } from '..';
import UnPairDeviceList from './UnPairDeviceList';

const UnPairDevicesFromRfids: FC<AssignDevicesToMembersModalStageProps> = ({
  state,
  selectedRfids,
  pairedDevices,
  setState,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const currentModalState = useAppSelector(
    modalState => modalState.ui.pairDevicesToRfidsModal,
  );

  const [searchString, setSearchString] = useState('');
  const [searchResults, setSearchResults] = useState<string[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setLoading] = useState<boolean>(false);

  const updatePairedDevices = useMemo(
    () => pairedDevices || [],
    [pairedDevices],
  );

  const selectedRfid = useAppSelector(s =>
    s.rfids.items.find(item => item.id === selectedRfids[0]),
  );

  const pairedDevicesList = useMemo(() => {
    return (selectedRfid?.pairedDevices ?? []).filter(device =>
      updatePairedDevices.includes(device.deviceId),
    );
  }, [selectedRfid?.pairedDevices, updatePairedDevices]);

  const fullStatus = useAppSelector(appState => appState.api.fullStatus);

  const devicesWithNames = useMemo(() => {
    return pairedDevicesList.map(device => ({
      ...device,
      name: fullStatus[device.serial]?.fna,
    }));
  }, [pairedDevicesList, fullStatus]);

  const fuse = useMemo(
    () =>
      new Fuse(devicesWithNames, {
        keys: ['name', 'serial'],
        threshold: 0.4,
      }),
    [devicesWithNames],
  );

  const handleSearch = useCallback((): void => {
    if (!searchString) {
      setSearchResults(null);

      return;
    }

    const results = fuse.search(searchString);

    setSearchResults(results.map(result => result.item.serial));
  }, [fuse, searchString]);

  const [debouncedSearch] = useDebounce(searchString, 500);

  useEffect(() => {
    if (debouncedSearch) {
      handleSearch();
    }
  }, [debouncedSearch, handleSearch]);

  const handleClearSearch = useCallback((): void => {
    setSearchString('');
    setSearchResults(null);
  }, []);

  useEffect(() => {
    const newAddedDevice = pairedDevicesList.find(
      device => device.serial === state.newDeviceSerial,
    );

    if (newAddedDevice?.deviceId) {
      setState(prev => ({ ...prev, devices: [newAddedDevice.deviceId] }));
    }
  }, [pairedDevicesList, setState, state.newDeviceSerial]);

  const filteredDevices = useMemo(
    () =>
      searchResults
        ? devicesWithNames.filter(device =>
            searchResults.includes(device.serial),
          )
        : devicesWithNames,
    [devicesWithNames, searchResults],
  );

  const handleUnPairDeviceFromRfid = useCallback(
    async (deviceId: number) => {
      try {
        setLoading(true);

        const result = await dispatch(
          unPairDevicesFromRfidsThunk({
            data: {
              rfid_ids: selectedRfids,
              device_ids: [deviceId],
            },
          }),
        ).unwrap();

        if (result.success) {
          await dispatch(getAllRfidsThunk({})).unwrap();
          dispatch(
            setPairDevicesToRfidsModal({
              ...currentModalState,
              pairedDevices: updatePairedDevices.filter(id => id !== deviceId),
            }),
          );
        } else {
          setError(t('common:pages.rfids.pair_devices.unpair_fail'));
        }
      } catch (e) {
        Sentry.captureException(e, {
          tags: { page: 'UnPairDevicesFromRfidModal' },
        });
      } finally {
        setLoading(false);
      }
    },
    [currentModalState, dispatch, selectedRfids, t, updatePairedDevices],
  );

  return (
    <Box
      sx={{
        height: '55vh',
        overflow: 'auto',
        paddingBottom: 5,
        width: '100%',
      }}
    >
      <Box mb={4}>
        <Typography variant="h2" mb={2} textAlign="center">
          {t('common:pages.rfids.pair_devices.paired_title')}
        </Typography>
      </Box>
      <Box mb={4} width="100%">
        <ListItemButton
          sx={{
            backgroundColor: theme.vars.palette.grey[100],
            borderRadius: 2,
          }}
          onClick={() => setState(prev => ({ ...prev, stage: 'pairDevices' }))}
        >
          <ListItemText
            color="primary"
            primaryTypographyProps={{
              style: {
                color: theme.vars.palette.primary.dark,
                fontWeight: 500,
              },
            }}
            primary={t('common:pages.rfids.pair_devices.pair_device')}
          />
          <IconButton edge="end" disableRipple>
            <AddCircleOutlineRoundedIcon color="primary" />
          </IconButton>
        </ListItemButton>
      </Box>
      <Box>
        <Box mb={3} width="100%">
          <CustomSearchbar
            search={searchString}
            setSearch={setSearchString}
            hasSearched={searchString.length > 0}
            handleSearch={handleSearch}
            handleClearSearch={handleClearSearch}
          />
        </Box>
        {filteredDevices.length > 0 ? (
          filteredDevices.map(item => (
            <UnPairDeviceList
              key={item.deviceId}
              device={item}
              isLoading={isLoading}
              handleUnPairDeviceFromRfid={handleUnPairDeviceFromRfid}
            />
          ))
        ) : (
          <Typography variant="body1" color="grey.600" textAlign="center">
            {t('common:pages.rfids.pair_devices.no_devices_found')}
          </Typography>
        )}
        <ErrorText error={error} />
      </Box>
    </Box>
  );
};

export default UnPairDevicesFromRfids;
